import React, { useState } from 'react';

import { eventPropsToString } from '@pray/shared/components/events/eventUtils';
import Button from '@pray/shared/components/ui/Button';
import Text from '@pray/shared/components/ui/Text';
import { RecurringPeriod } from '@pray/shared/data/pricing/types';
import { formatCurrency, formatNumber } from '@pray/shared/utils';
import { cn } from '@pray/shared/utils/styles';

import { buttons, colors, typography } from '../../foundations';
import Loading from '../../Loading';
import { Check } from '../Icons/Check';
import { ChevronDown } from '../Icons/ChevronDown';
import Label from '../Label';

export type PricingCardProps = {
  plan: PricingCardPlan;
  billingPeriod: RecurringPeriod;
  maxFeatures?: number;
  isCurrent?: boolean;
  isUpgrade?: boolean;
  isLoading?: boolean;
  className?: string;
  onPlanSelect?: () => void;
};

export type PricingCardPlan = {
  name: string;
  description: string;
  originalPrice?: number;
  price: number;
  credits: number;
  isPopular?: boolean;
  features: string[];
  sku: string;
  isFreeTrialEnabled: boolean;
  freeTrialInterval?: 'day' | 'week' | 'month' | 'year';
  freeTrialIntervalCount?: number;
  freeTrialCreditsAmount?: number;
};

export const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  billingPeriod,
  maxFeatures = 12,
  isCurrent = false,
  isUpgrade = false,
  isLoading = false,
  className = '',
  onPlanSelect,
}) => {
  const {
    name,
    description,
    originalPrice,
    price,
    credits,
    features,
    isPopular,
    isFreeTrialEnabled,
    freeTrialInterval,
    freeTrialIntervalCount,
    freeTrialCreditsAmount,
  } = plan || {};

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const isEnterprise = !price;
  const isPopularAndGetStarted = isPopular && !isCurrent && !isUpgrade;

  const formatTrialPeriod = () => {
    if (!isFreeTrialEnabled || !freeTrialInterval || !freeTrialIntervalCount) return '';

    const intervalText = freeTrialIntervalCount === 1 ? freeTrialInterval : `${freeTrialInterval}s`;
    return `${freeTrialIntervalCount}-${intervalText}`;
  };

  const handlePlanSelect = () => {
    if (isEnterprise) {
      window.location.href = 'https://hubs.li/Q03M8CkC0';
      return;
    }

    onPlanSelect?.();
  };

  const getButtonText = () => {
    if (isEnterprise) return 'CONTACT SALES';
    if (isCurrent) return 'CURRENT PLAN';
    if (isUpgrade) return `UPGRADE TO ${name}`;
    if (isFreeTrialEnabled && !isCurrent && !isUpgrade) return 'TRY NOW FOR FREE';
    return 'GET STARTED';
  };

  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border border-solid border-[var(--theme-border-divider)] p-8 hover:border-[var(--theme-accent-primary)]',
        isPopularAndGetStarted || isUpgrade ? 'bg-[var(--theme-bg-secondary)]' : 'bg-bg-primary',
        className
      )}
    >
      {isLoading || !plan ? (
        <>
          <div className="relative mb-6 flex flex-col">
            <Loading width={130} height={40} />
            <Loading width={130} height={20} />
          </div>

          <div className="flex flex-col">
            <Loading width={100} height={30} />
            <Loading width={80} height={20} />

            <Loading height={40} />

            <div className="mt-6 border-t border-[var(--theme-border-divider)]" />

            <div className="mt-6">
              <Loading width={150} height={40} />
              <div className="flex flex-col">
                <Loading width={200} height={20} />
                <Loading width={200} height={20} />
                <Loading width={200} height={20} />
                <Loading width={200} height={20} />
                <Loading width={200} height={20} />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="relative mb-6 flex flex-col">
            <Text className={isPopularAndGetStarted && 'whitespace-nowrap'}>
              <Text variant={typography.heading_3xl} className="mb-1">
                {name}
              </Text>
              {isPopularAndGetStarted && (
                <Label className="ml-3 bg-[var(--theme-accent-tertiary)] align-super text-[var(--theme-accent-primary)]">
                  <Text variant={typography.button_small}>MOST POPULAR</Text>
                </Label>
              )}
            </Text>
            <Text variant={typography.body_lg}>{description}</Text>
          </div>

          <div className="mb-6">
            <Text variant={typography.heading_sm} className="text-[var(--theme-text-tertiary)] line-through">
              {originalPrice ? formatCurrency(originalPrice) : ' '}
            </Text>

            <div className="flex items-end">
              <Text className="whitespace-pre">
                <Text variant={typography.heading_2xl}>{!isEnterprise ? formatCurrency(price) : ' '}</Text>
                {!isEnterprise && (
                  <Text variant={typography.heading_sm} className="">
                    /mo
                  </Text>
                )}
                {!isEnterprise && billingPeriod === RecurringPeriod.YEARLY && (
                  <Text variant={typography.body_md} className="text-[var(--theme-text-tertiary)]">
                    {' billed yearly'}
                  </Text>
                )}
              </Text>
            </div>
            <div className="flex items-end">
              <Text color={colors.text_secondary} className="whitespace-pre">
                <Text variant={typography.body_lg}>{credits ? `${formatNumber(credits)} credits` : ' '}</Text>
                {!!credits && <Text variant={typography.body_md}> /mo</Text>}
              </Text>
            </div>
          </div>

          <Button
            variant={isPopularAndGetStarted || isUpgrade ? buttons.variant.primary : buttons.variant.secondary}
            color={isPopularAndGetStarted || isUpgrade ? colors.pray_featured : colors.tertiary}
            disabled={isCurrent}
            data-tapeventname="Select Plan"
            data-eventprops={eventPropsToString({
              price: plan.price,
              sku: plan.sku,
            })}
            onClick={handlePlanSelect}
          >
            {getButtonText()}
          </Button>

          {isFreeTrialEnabled && !isCurrent && !isUpgrade && (
            <Text variant={typography.body_sm} color={colors.text_secondary} className="mt-2 text-center">
              {formatTrialPeriod()} trial with {formatNumber(freeTrialCreditsAmount)} credits
            </Text>
          )}

          <div className={cn('border-t border-[var(--theme-border-divider)] mt-8', isFreeTrialEnabled && 'mt-2')} />

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <Text variant={typography.heading_lg} className="mb-4">
                Credits usable for either
              </Text>
              <button
                type="button"
                aria-expanded={isExpanded}
                aria-controls={`features-list-${plan.name}`}
                className={cn(
                  'cursor-pointer md:!hidden flex items-center gap-2 mb-3',
                  features.length < maxFeatures && '!hidden'
                )}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Text variant={typography.heading_sm}>{isExpanded ? 'See less' : 'See more'}</Text>
                <ChevronDown
                  className={cn(
                    'transition-all duration-100 stroke-[var(--theme-text-tertiary)]',
                    isExpanded && 'rotate-180'
                  )}
                />
              </button>
            </div>
            <ul
              id={`features-list-${plan.name}`}
              className={cn('space-y-3 overflow-hidden md:max-h-none', isExpanded ? 'max-h-none' : 'max-h-[260px]')}
            >
              {features?.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <Check color="var(--color-text-primary)" />
                  <Text variant={typography.body_md} className="text-primary-inverse font-[450]">
                    {feature}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
